/**
 * Party work orders section (issue #2519).
 *
 * The work orders a person or company has ordered. Used by both the person
 * card and the company card — exactly one of `personId` / `companyId`.
 *
 * Rows come from `GET /bookings?personId=|companyId=`, which matches the
 * `orderer` party in `work_order_parties` (#833 C2). Only the orderer counts:
 * being the billing party or the pickup person on someone else's work order
 * does not put it on this list.
 *
 * No amount column on purpose. `bookings.total_cost_cents` is written net by
 * some paths and gross by others, so a figure here would be wrong for part of
 * the rows — the work order itself shows the money that has been reconciled.
 */

import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { format, isValid } from 'date-fns';
import { DetailSection } from '@/components/common/DetailSection';
import { EntityListResults } from '@/components/common/EntityListResults';
import { RegistrationPlate } from '@/components/common/RegistrationPlate';
import { StatusBadge, type WorkOrderStatus } from '@/components/common/StatusBadge';
import { useBookingsPage, type BookingListItem } from '@/hooks/useBookings';
import { useOpenWorkOrder, useViewedWorkOrderId } from '@/hooks/useWorkOrderWindow';
import { dateFnsLocale } from '@/utils/locale';

/** Modified and middle clicks stay with the anchor (#4108). */
function isModifiedClick(event: React.MouseEvent): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

export interface PartyWorkOrdersSectionProps {
  personId?: string;
  companyId?: string;
}

export function PartyWorkOrdersSection({ personId, companyId }: PartyWorkOrdersSectionProps) {
  const { t } = useTranslation('parties');
  const openWorkOrder = useOpenWorkOrder();
  const viewedWorkOrderId = useViewedWorkOrderId();

  const dateLocale = dateFnsLocale();

  const query = useBookingsPage(
    {
      ...(personId ? { personId } : {}),
      ...(companyId ? { companyId } : {}),
    },
    // Without a party the query would list every work order in the workshop.
    { enabled: Boolean(personId ?? companyId) },
  );

  const workOrders = query.rows;

  const secondary = (workOrder: BookingListItem) => {
    const parts: string[] = [];
    const scheduled = new Date(workOrder.scheduledAt);
    if (isValid(scheduled)) {
      parts.push(format(scheduled, 'dd.MM.yyyy', { locale: dateLocale }));
    }
    if (workOrder.vehicleMakeModel) {
      parts.push(workOrder.vehicleMakeModel);
    }
    if (workOrder.mechanicName) {
      parts.push(workOrder.mechanicName);
    }
    return parts.join(' · ');
  };

  return (
    <DetailSection title={t('workOrders.title')}>
      <EntityListResults
        loading={query.isLoading}
        isError={query.isError}
        errorText={t('workOrders.loadError')}
        resultCountText={t('workOrders.resultCount', { count: query.total })}
        /* The wrapper already says it is loading; no footer for an empty list. */
        {...(query.total > 0 ? { pagination: query.pagination } : {})}
      >
        {query.isLoading ? null : workOrders.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {t('workOrders.none')}
          </Typography>
        ) : (
          <List disablePadding>
            {workOrders.map((workOrder) => (
              <ListItem key={workOrder.id} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={`/workorders/${workOrder.id}`}
                  selected={workOrder.id === viewedWorkOrderId}
                  onClick={(event: React.MouseEvent) => {
                    if (isModifiedClick(event)) return;
                    event.preventDefault();
                    openWorkOrder(workOrder.id);
                  }}
                  sx={{ color: 'inherit', textDecoration: 'none' }}
                >
                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Typography variant="body2" component="span" fontWeight={600}>
                          {workOrder.workOrderNumber ?? t('workOrders.noNumber')}
                        </Typography>
                        {workOrder.vehicleRegNumber && (
                          <RegistrationPlate registrationNumber={workOrder.vehicleRegNumber} />
                        )}
                      </Stack>
                    }
                    secondary={secondary(workOrder) || undefined}
                  />
                  <Box sx={{ ml: 1, flexShrink: 0 }}>
                    <StatusBadge status={workOrder.status as WorkOrderStatus} />
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </EntityListResults>
    </DetailSection>
  );
}
